import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, Kpi, Skeleton, ErrorBox, Badge } from "@/components/ui";

interface DashboardSummary {
  active_patients: number;
  urgent_patients: number;
  open_reminders: number;
  overdue_tasks: number;
  needs_status_update: number;
  reports_pending_review: number;
  appointments_pending: number;
  tests_pending: number;
  rt_decision_pending: number;
  ct_sim_pending: number;
  on_treatment_patients: number;
  follow_up_due: number;
  pending_ai_drafts: number;
}

export default function Dashboard() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["dashboard"],
    queryFn: () => api.get<DashboardSummary>("/analytics/dashboard"),
  });
  const qc = useQueryClient();
  const runWatcher = useMutation({
    mutationFn: () => api.post("/watchers/run-status-watcher"),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["dashboard"] }),
  });

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-slate-50">
            Command Center
          </h1>
          <div className="text-sm text-slate-400">
            Collect broadly. Display intelligently. Escalate only what matters.
          </div>
        </div>
        <div className="flex gap-2">
          <Link to="/registry" className="btn-primary">
            Open Patient Files
          </Link>
          <button
            className="btn-ghost"
            disabled={runWatcher.isPending}
            onClick={() => runWatcher.mutate()}
          >
            {runWatcher.isPending ? "Scanning…" : "Run Status Watcher"}
          </button>
        </div>
      </div>

      {error ? <ErrorBox error={error} /> : null}
      {isLoading || !data ? (
        <Skeleton rows={6} />
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            <Kpi label="Active Patient Files" value={data.active_patients} tone="info" />
            <Kpi label="Urgent" value={data.urgent_patients} tone="warn" />
            <Kpi
              label="Needs status update"
              value={data.needs_status_update}
              tone={data.needs_status_update > 0 ? "warn" : "ok"}
            />
            <Kpi label="Open reminders" value={data.open_reminders} />
            <Kpi
              label="Overdue tasks"
              value={data.overdue_tasks}
              tone={data.overdue_tasks > 0 ? "bad" : "ok"}
            />
            <Kpi
              label="Reports pending review"
              value={data.reports_pending_review}
              tone={data.reports_pending_review > 0 ? "warn" : "ok"}
            />
            <Kpi
              label="Appointments pending"
              value={data.appointments_pending}
            />
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            <Card title="RT workflow queues">
              <ul className="space-y-2 text-sm">
                <Row label="RT decision pending" value={data.rt_decision_pending} />
                <Row label="CT simulation pending" value={data.ct_sim_pending} />
                <Row label="On treatment" value={data.on_treatment_patients} />
                <Row label="Follow-up due" value={data.follow_up_due} />
                <Row label="Tests pending" value={data.tests_pending} />
              </ul>
            </Card>
            <Card title="Quick links">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <Link to="/registry" className="btn-ghost">Patient registry</Link>
                <Link to="/knowledge" className="btn-ghost">Knowledge library</Link>
                <Link to="/style" className="btn-ghost">Style library</Link>
                <Link to="/tasks" className="btn-ghost">Tasks</Link>
                <Link to="/appointments" className="btn-ghost">Appointments</Link>
                <Link to="/analytics" className="btn-ghost">Monthly analytics</Link>
              </div>
              <div className="mt-4 text-xs text-amber-300/80">
                <Badge tone="warn">Reminder</Badge>{" "}
                Patient File is the normal review/edit surface for AI-authored entries.
              </div>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}

function Row({ label, value }: { label: string; value: number }) {
  return (
    <li className="flex items-center justify-between border-b border-line/60 py-1">
      <span className="text-slate-300">{label}</span>
      <span className="font-semibold text-slate-100">{value}</span>
    </li>
  );
}
